/**
 * Shared primitives for reading `DataAccessPolicy` column-rule trees back into
 * ConditionBuilder form values.
 *
 * Three features persist conditions through the same `columnRules` shape but
 * each had its own private copy of this logic — MCP Registry
 * (`mapPolicyFormValues.ts`), Unified Lakehouse (`createPolicyConditionHelpers.ts`)
 * and Enterprise Policies (which borrowed the ULH one and inherited its
 * config-blindness). The differences between them are genuine (different
 * attribute-group id conventions, different match-with builders), so what lives
 * here is only the part that must not drift: tree unwrapping, attribute
 * resolution against a live attribute list, and value reconstruction.
 *
 * Pure TypeScript, no React/side effects.
 */
import {
  type ConditionAttributeOption,
  type ConditionOption,
  type MatchWithOption,
} from "@/components/ConditionBuilder";

/**
 * Structural minimum shared by the three near-identical generated rule types
 * (`ColumnRuleDetail`, `DataAccessPolicyRuleColumns`,
 * `DataAccessPolicyRuleColumnsModel`), so callers can pass whichever their API
 * response carries without a cast.
 */
export type ColumnRuleNode = {
  conjunction?: string;
  columnRules?: ColumnRuleNode[];
};

/**
 * Extracts the inner condition array and top-level combinator from a policy's
 * `rules`.
 *
 * Structure produced by the payload writers:
 *   rules[0].columnRules[0]             ← outer wrapper (holds the combinator)
 *   rules[0].columnRules[0].columnRules ← the actual condition entries
 */
export function extractInnerRules<T extends ColumnRuleNode>(
  rules: Array<{ columnRules?: T[] }> | undefined,
): { combinator: "AND" | "OR"; rules: T[] } {
  const outerRule = rules?.[0]?.columnRules?.[0];
  return {
    combinator: outerRule?.conjunction === "AND" ? "AND" : "OR",
    rules: (outerRule?.columnRules as T[] | undefined) ?? [],
  };
}

interface ResolveConditionAttributeArgs {
  /** The persisted `elementName` — the attribute's API identifier. */
  elementName: string | undefined;
  /** The persisted `elementType`, used to disambiguate same-named columns. */
  elementType: string | undefined;
  /** Live attribute list from the feature's condition-builder config. */
  attributes: ConditionAttributeOption[];
  /**
   * Maps a persisted `elementType` to the group id used by this feature's
   * config. MCP collapses to bare "USER"/"AGENT"; Enterprise group ids ARE the
   * element types, so it passes the identity function.
   */
  groupIdFor: (elementType?: string) => string;
  /** What lands on `.type` when nothing resolves. */
  fallbackType: string;
}

/**
 * Resolves a full `ConditionAttributeOption` by looking `elementName` up in the
 * live attributes list. Falls back to a minimal object when no match is found.
 *
 * Without this, a reloaded row renders the raw API identifier: the dropdowns
 * label themselves from the *stored* option object (see `FormDropDownList` and
 * `getOptionLabel`), never from the matching item in `data`, so whatever the
 * reader produces is what the user sees permanently.
 */
export function resolveConditionAttributeOption({
  elementName,
  elementType,
  attributes,
  groupIdFor,
  fallbackType,
}: ResolveConditionAttributeArgs): ConditionAttributeOption {
  if (elementName) {
    // 1. Search top-level attributes (flat mode or direct-selection leaves)
    const found = attributes.find(a => a.name === elementName);
    if (found) return found;

    // 2. Search sub-items — prefer the group matching elementType to avoid
    //    mis-resolving when two groups share an attribute name. When found,
    //    merge dynamic fields from resolveSubAttribute so the returned object
    //    carries the correct valueType needed by reconstructConditionValue.
    const targetGroupId = groupIdFor(elementType);
    const orderedGroups = [
      ...attributes.filter(g => g.id === targetGroupId),
      ...attributes.filter(g => g.id !== targetGroupId),
    ];
    for (const group of orderedGroups) {
      if (!group.subAttributes) continue;
      const sub = group.subAttributes.find(
        sa => sa.name === elementName || sa.id === elementName,
      );
      if (sub) {
        const resolved = group.resolveSubAttribute?.(sub);
        return resolved ? { ...sub, ...resolved } : sub;
      }
    }
  }

  // 3. Last-resort fallback — `fallbackType` keeps the attribute placeable in
  //    the correct hierarchical group under the caller's own convention.
  return {
    id: elementName ?? "",
    name: elementName ?? "",
    type: fallbackType,
  };
}

/**
 * Reconstructs the form value from an API raw string.
 *
 * The render type comes from the selected Match With option first (mirrors
 * ConditionValueField's precedence), then the attribute as fallback:
 * - multi-select → ConditionOption[] (one per comma-separated id)
 * - single-select → ConditionOption[] of length 1
 * - text / text-area / unknown → plain string
 */
export function reconstructConditionValue(
  rawValue: string | undefined,
  matchWith: MatchWithOption | null,
  attr: ConditionAttributeOption | undefined,
): string | ConditionOption[] {
  const value = rawValue ?? "";
  const valueType = matchWith?.valueType ?? attr?.valueType;

  /* Length-guarded, not `??`: buildMcpMatchWithOption always sets
     `valueOptions` on the attribute-picker branches, using `?? []` when no
     picker callback was supplied. A nullish check would let that empty array
     win and discard the attribute's own options. */
  const options = matchWith?.valueOptions?.length
    ? matchWith.valueOptions
    : (attr?.valueOptions ?? []);

  if (valueType === "multi-select") {
    const ids = value
      .split(",")
      .map(s => s.trim())
      .filter(Boolean);
    const optMap = new Map(options.map(o => [o.id, o]));
    return ids.map(id => optMap.get(id) ?? { id, name: id });
  }

  if (valueType === "single-select") {
    // Guard the empty string: an unconditional wrap yields a phantom blank chip.
    if (!value) return [];
    const opt = options.find(o => o.id === value);
    return [opt ?? { id: value, name: value }];
  }

  return value;
}
